import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useCart } from '../contexts/CartContext';
import { api } from '../utils/api';
import ImageWithPlaceholder from './ImageWithPlaceholder';
import { normalizeCheckoutsResponse, savePendingCheckouts } from '../utils/checkoutPending';

export default function CartPanel({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { getToken, isSignedIn } = useAuth();
  const { items, updateQuantity, removeItem, saveCartForCheckout, restoreFromPendingCheckout } = useCart();
  const [itemDetails, setItemDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pendingMulti, setPendingMulti] = useState(null);

  useEffect(() => {
    if (!isOpen || items.length === 0) {
      setItemDetails({});
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const detailsPromises = items.map(async (cartItem) => {
          try {
            const res = await api(`/api/items/${cartItem.id}`, {});
            if (!res.ok) throw new Error(`Failed to load item ${cartItem.id}`);
            const data = await res.json();
            return { id: cartItem.id, data };
          } catch (err) {
            return { id: cartItem.id, data: null, error: err.message };
          }
        });

        const details = await Promise.all(detailsPromises);
        if (!cancelled) {
          const detailsMap = {};
          details.forEach(({ id, data, error: err }) => {
            detailsMap[id] = data
              ? {
                  title: data.title,
                  price: data.price,
                  imageUrls: data.imageUrls || (data.imageUrl ? [data.imageUrl] : []),
                  stockCount: data.stockCount,
                  vendorId: data.vendorId,
                }
              : { error: err || 'Failed to load' };
          });
          setItemDetails(detailsMap);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, items]);

  useEffect(() => {
    if (isOpen && isSignedIn) restoreFromPendingCheckout();
  }, [isOpen, isSignedIn, restoreFromPendingCheckout]);

  const subtotal = items.reduce((sum, cartItem) => {
    const details = itemDetails[cartItem.id];
    if (!details || details.error) return sum;
    return sum + (details.price || 0) * cartItem.quantity;
  }, 0);

  const handleQuantityChange = (id, delta) => {
    const cartItem = items.find((item) => item.id === id);
    if (!cartItem) return;
    const newQuantity = cartItem.quantity + delta;
    if (newQuantity <= 0) removeItem(id);
    else updateQuantity(id, newQuantity);
  };

  const hasStockIssues = () =>
    items.some((cartItem) => {
      const details = itemDetails[cartItem.id];
      if (!details || details.error) return false;
      const stockCount = details.stockCount ?? 0;
      return stockCount <= 0 || cartItem.quantity > stockCount;
    });

  const startCheckoutAt = (checkouts, index) => {
    savePendingCheckouts(checkouts, index);
    window.location.href = checkouts[index].checkoutUrl;
  };

  const handleCheckout = async () => {
    if (items.length === 0) return;

    if (!isSignedIn) {
      saveCartForCheckout(items);
      onClose?.();
      navigate(`/sign-in?returnUrl=${encodeURIComponent(window.location.href)}`);
      return;
    }

    if (hasStockIssues()) {
      setError('Some items in your cart are out of stock or have insufficient quantity. Please update your cart.');
      return;
    }

    setCheckoutLoading(true);
    setError(null);
    setPendingMulti(null);
    try {
      const cartItems = items.map(({ id, quantity }) => ({ itemId: id, quantity }));
      const res = await api('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartItems }),
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.details && Array.isArray(data.details)) {
          setError(`Stock issues: ${data.details.join('; ')}`);
        } else {
          setError(data.error || data.stripeError || data.message || `Checkout failed (${res.status})`);
        }
        return;
      }

      const checkouts = normalizeCheckoutsResponse(data);
      if (checkouts.length === 0) {
        setError('No checkout session returned.');
        return;
      }

      if (checkouts.length === 1) {
        startCheckoutAt(checkouts, 0);
        return;
      }

      setPendingMulti(checkouts);
    } catch (err) {
      setError(err.message || 'Checkout failed.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[45]" onClick={onClose} aria-hidden="true" />

      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold">Shopping Cart</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close cart"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {pendingMulti && (
            <div className="mb-4 rounded-xl border-2 border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-950/40 p-4 flex flex-col gap-3">
              <p className="font-semibold text-indigo-900 dark:text-indigo-100">
                You&apos;ll pay {pendingMulti.length} vendors separately
              </p>
              <p className="text-sm text-indigo-800/90 dark:text-indigo-200/90">
                Each booth has its own Stripe account, so checkout happens one vendor at a time.
              </p>
              <ul className="space-y-2 text-sm">
                {pendingMulti.map((c, i) => (
                  <li key={`${c.vendorId}-${i}`} className="flex justify-between gap-2">
                    <span>
                      <span className="font-medium">{i + 1}. {c.vendorName}</span>
                      {c.booth ? ` · Booth ${c.booth}` : ''}
                      {c.itemCount ? ` · ${c.itemCount} item${c.itemCount !== 1 ? 's' : ''}` : ''}
                    </span>
                    <span className="font-semibold shrink-0">${Number(c.subtotal).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => startCheckoutAt(pendingMulti, 0)}
                className="w-full py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
              >
                Pay vendor 1 of {pendingMulti.length}
                {pendingMulti[0]?.vendorName ? ` — ${pendingMulti[0].vendorName}` : ''}
              </button>
              <button
                type="button"
                onClick={() => setPendingMulti(null)}
                className="w-full py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:underline"
              >
                Cancel
              </button>
            </div>
          )}

          {loading && items.length > 0 && (
            <p className="text-center text-slate-500 py-8">Loading items...</p>
          )}

          {error && (
            <div className="rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 mb-4">
              {error}
            </div>
          )}

          {!loading && items.length === 0 && (
            <p className="text-center text-slate-500 py-12">Your cart is empty</p>
          )}

          {!loading &&
            items.map((cartItem) => {
              const details = itemDetails[cartItem.id];
              const imageUrl = details?.imageUrls?.[0];
              const title = details?.title || 'Loading...';
              const price = details?.price || 0;
              const stockCount = details?.stockCount ?? 0;
              const bad = details?.error || stockCount <= 0 || cartItem.quantity > stockCount;

              return (
                <div key={cartItem.id} className="flex gap-4 p-4 border-b border-slate-200 dark:border-slate-700 last:border-b-0">
                  <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800">
                    {imageUrl ? (
                      <ImageWithPlaceholder
                        src={imageUrl}
                        alt={title}
                        className="w-full h-full object-contain"
                        containerClassName="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">No image</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-medium truncate">{title}</h3>
                      <button type="button" onClick={() => removeItem(cartItem.id)} className="text-slate-400 hover:text-red-600" aria-label="Remove">
                        ×
                      </button>
                    </div>
                    <p className="text-indigo-600 font-semibold">${Number(price).toFixed(2)}</p>
                    {bad && (
                      <p className="text-sm text-red-600 mt-1">
                        {details?.error || (stockCount <= 0 ? 'Out of stock' : 'Not enough stock')}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button type="button" onClick={() => handleQuantityChange(cartItem.id, -1)} className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800" aria-label="Decrease">
                        −
                      </button>
                      <span className="w-12 text-center font-medium">{cartItem.quantity}</span>
                      <button type="button" onClick={() => handleQuantityChange(cartItem.id, 1)} className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800" aria-label="Increase">
                        +
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {items.length > 0 && !pendingMulti && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-4 pb-bottom-nav space-y-4">
            <div className="flex justify-between text-lg font-semibold">
              <span>Subtotal:</span>
              <span className="text-indigo-600">${subtotal.toFixed(2)}</span>
            </div>
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutLoading || hasStockIssues()}
              className="w-full py-4 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {checkoutLoading ? 'Preparing checkout…' : hasStockIssues() ? 'Fix stock issues to checkout' : 'Proceed to Checkout'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
